import { clsx, type ClassValue } from "clsx"
import { extendTailwindMerge } from "tailwind-merge"

/**
 * The design system's semantic type rungs, taught to tailwind-merge.
 *
 * This is not bookkeeping — without it `cn()` silently deletes colours.
 *
 * tailwind-merge decides which of two conflicting classes survives by sorting
 * every utility into a group, and it knows the STOCK font sizes by name
 * (`text-sm`, `text-2xl`…). It has never heard of `text-body-lg` or
 * `text-body`, so it falls through to the only other thing `text-*` can be —
 * a colour — and files them under `text-color`. A component's own colour and
 * a call site's size are then the same decision, and the last one wins:
 *
 *     cn("bg-primary text-primary-foreground", "h-12 text-body")
 *       → "bg-primary h-12 text-body"          // the white label is gone
 *
 * PropPay LX shipped exactly that: every copper button rendered its label in
 * the default dark foreground. SaleFish has sixty call sites on these rungs
 * today, each one `className` away from the same bug. Registering the scale
 * here puts size and colour back in separate groups, so both survive.
 *
 * Keep this list in step with the semantic aliases in `fontSize` in
 * tailwind.config.ts — the stock sizes need no entry, tailwind-merge already
 * knows them. tests/tailwindMerge.test.ts pins the two together.
 */
export const TYPE_SCALE = [
  "micro",
  "caption",
  "label",
  "body",
  "body-lg",
  "title",
  "h3",
  "h2",
  "h1",
  // compact ramp — dense tabular surfaces
  "t-head",
  "t-cell",
  "t-lead",
  // form controls (15px pointer / 16px finger)
  "control",
] as const

const twMerge = extendTailwindMerge({
  extend: {
    classGroups: {
      "font-size": [{ text: [...TYPE_SCALE] }],
    },
  },
})

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

/**
 * Converts a country code to its corresponding flag emoji
 * @param countryCode - ISO 3166-1 alpha-2 country code (e.g., "us", "ca", "gb")
 * @returns The flag emoji for the country
 */
export function getCountryFlag(countryCode: string): string {
  if (!countryCode || countryCode.length !== 2) {
    return "";
  }
  
  // Convert country code to uppercase
  const code = countryCode.toUpperCase();
  
  // Special case for UK (often represented as "uk" but should be "gb" for flag)
  const flagCode = code === "UK" ? "GB" : code;
  
  // Convert to flag emoji using regional indicator symbols
  // Regional indicator symbols start at 0x1F1E6 (🇦)
  const codePoints = flagCode
    .split("")
    .map(char => 0x1F1E6 + char.charCodeAt(0) - 65);
  
  return String.fromCodePoint(...codePoints);
}

/**
 * Converts a string to proper case (first letter uppercase, rest lowercase)
 * @param text - The text to convert
 * @returns The text in proper case
 */
export function toProperCase(text: string | null | undefined): string {
  if (!text) return "";
  return text.charAt(0).toUpperCase() + text.slice(1).toLowerCase();
}

/**
 * Normalizes a URL path by removing query strings, hash fragments, and trailing slashes
 * @param location - The location string to normalize (from wouter's useLocation)
 * @returns The normalized path
 */
export function normalizeLocation(location: string): string {
  if (!location) return "/";
  
  // Remove query strings (everything after ?)
  let normalized = location.split('?')[0];
  
  // Remove hash fragments (everything after #)
  normalized = normalized.split('#')[0];
  
  // Remove trailing slashes (but keep root /)
  if (normalized.length > 1) {
    normalized = normalized.replace(/\/$/, '');
  }
  
  // Fallback to root if empty
  return normalized || "/";
}
